import {Image} from 'components/manufacturer/ManufacturerDefaults/store/manufacturerDefaultsApi';
import React, {useCallback, useRef} from 'react';
import LogoUpload, {Thumbnail} from 'shared/components/Settings/LogoUpload';
import {
    InputFieldType,
    SettingContainer,
    InputGroup,
} from 'shared/components/Settings/SettingsPanel';
import {Flex} from 'shared/components/StyledComponents';
import Icon from 'shared/Icon';
import styled from 'styled-components';

const ImageField = ({
    field,
    imageValue,
    isCustomer,
    onResetDefault,
    onThumbailClick,
}: {
    field: InputFieldType;
    imageValue: Image;
    isCustomer: boolean;
    onResetDefault: () => void;
    onThumbailClick: () => void;
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const handleImageClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const resolvePreviewImage = () => {
        const imageUrl: string =
            imageValue?.imageUrl ?? imageValue?.defaultImageUrl;
        if (field.type === 'image') {
            return imageUrl;
        }

        // https://www.svgrepo.com/collection/files-types/
        const extension = imageUrl?.split('.').at(-1);
        switch (extension) {
            case 'csv':
                return '/templates/file-csv.svg';
            case 'xls':
            case 'xlsx':
                return '/templates/file-xls.svg';
            default:
                return '/templates/file-unknown.svg';
        }
    };

    return (
        <InputGroup>
            <SettingContainer $width="60%">
                <label>{field.label}</label>
                {!!field.infoText ? <p>{field.infoText}</p> : null}
            </SettingContainer>
            <SettingContainer style={{alignItems: 'end'}}>
                {isCustomer ? (
                    <LogoUpload
                        imgSrc={imageValue?.imageUrl}
                        height={field?.inputHeight}
                        onSaveCallback={field?.onSaveField}
                        fileInputRef={fileInputRef}
                        handleImageClick={handleImageClick}
                        limit={field?.fileLimit}
                    />
                ) : (
                    <Thumbnail
                        $height={field?.inputHeight}
                        src={resolvePreviewImage()}
                        onClick={onThumbailClick}
                    />
                )}

                {/* Display the image filename for non-images */}
                {field?.type === 'file-upload' &&
                typeof imageValue?.imageUrl === 'string' ? (
                    <p>{imageValue.imageUrl.split('/').at(-1)}</p>
                ) : null}

                <Flex $gap="10px">
                    <ActionIconContainer
                        onClick={
                            isCustomer ? handleImageClick : onThumbailClick
                        }>
                        <Icon width="19px" iconName="Cloud-Upload.svg" />
                        Upload
                    </ActionIconContainer>
                    {!imageValue?.isDefault ? (
                        <ActionIconContainer onClick={onResetDefault}>
                            <Icon width="16px" iconName="Reverse-Refresh.svg" />
                            Default
                        </ActionIconContainer>
                    ) : null}
                </Flex>
            </SettingContainer>
        </InputGroup>
    );
};

const ActionIconContainer = styled.div`
    display: flex;
    gap: 5px;
    align-items: center;
    &:hover {
        cursor: pointer;
        color: ${({theme}) => theme.colors.primary.main};
    }
`;

export default ImageField;
